import React, { useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AppBar,
    Button,
    Dialog,
    IconButton,
    List,
    ListItem,
    Slide,
    Toolbar,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Close, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { logsFilterState, logsOpenState, logsState } from '../../common/recoil/UserInfo/atoms';
import { remoteNamesState } from '../../common/recoil/websocket/atoms';
import { getSessionDebugEntries } from '../../common/utils/sessionDebug';

const useStyles = makeStyles((theme) => ({
	appBar: {
		position: 'relative',
	},
	title: {
		marginLeft: theme.spacing(2),
		flex: 1,
	},
	accordion: {
		flexDirection: 'column',
	},
}));

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

const LogViewer = ({ retrieveLogs }) => {
	const classes = useStyles();
	const [open, setOpen] = useRecoilState(logsOpenState);
	const [logs, setLogs] = useRecoilState(logsState);
	const [logsFilter, setLogsFilter] = useRecoilState(logsFilterState);
	const remoteNames = useRecoilValue(remoteNamesState);
	const { t } = useTranslation();
	const handleClose = () => {
		setOpen(false);
		setLogsFilter(null);
	};

	useEffect(() => {
		if (open) {
			const tempLogs = retrieveLogs(logsFilter?.ids || []);
			tempLogs.map((x) => {
				const remoteName = remoteNames.find((y) => y.id === x.id)?.name;
				x.name = remoteName ? `${remoteName} (${x.id})` : x.name || x.id;
			});

			const sessionEntries = getSessionDebugEntries();
			const diagnosticsEntry = {
				id: 'local-session-debug',
				name: 'Local diagnostics',
				logs: [
					{
						type: 'meta',
						at: new Date().toISOString(),
						pathname: window.location.pathname,
						userAgent: window.navigator.userAgent,
						language: window.navigator.language,
					},
					...sessionEntries,
				],
			};

			setLogs([diagnosticsEntry, ...tempLogs]);
		}
	}, [open, logsFilter, remoteNames]);

	const getPrettyLog = (log) => {
		let prettyLog = '';
		for (let key in log) {
			if (log.hasOwnProperty(key)) {
				const value =
					typeof log[key] === 'object' && log[key] !== null
						? JSON.stringify(log[key])
						: log[key];
				prettyLog += ` ${key} : ${value} /`;
			}
		}
		return prettyLog;
	};

	const copyLogs = async (targetLogs) => {
		try {
			await navigator.clipboard.writeText(JSON.stringify(targetLogs, null, 2));
		} catch (error) {
			console.error('Error copying logs to clipboard', error);
		}
	};

	const downloadLogs = (targetLogs) => {
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
		const blob = new Blob([JSON.stringify(targetLogs, null, 2)], {
			type: 'application/json',
		});
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `horrol-diagnostics-${timestamp}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	};

	return (
        <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
			<AppBar className={classes.appBar}>
				<Toolbar>
					<IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                        size="large">
						<Close />
					</IconButton>
					<Typography variant="h6" className={classes.title}>
						{logsFilter?.title || 'Logs'}
					</Typography>
					<Button color="inherit" onClick={() => copyLogs(logs)}>
						{t('streamMenu.copyDiagnostics')}
					</Button>
					<Button color="inherit" onClick={() => downloadLogs(logs)}>
						{t('streamMenu.downloadDiagnostics')}
					</Button>
				</Toolbar>
			</AppBar>
			{logs.map((x) => (
				<Accordion className={classes.accordion} key={x.id}>
					<AccordionSummary
						expandIcon={<ExpandMore />}
						aria-controls="panel1a-content"
						id={x.name}>
						<Typography className={classes.heading}>{x.name || x.id}</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Button size="small" onClick={() => copyLogs(x.logs)}>Copy section</Button>
						<List>
							{x.logs.map((log, index) => (
								<ListItem key={`log-${index}`}>Log: {getPrettyLog(log)}</ListItem>
							))}
						</List>
					</AccordionDetails>
				</Accordion>
			))}
		</Dialog>
    );
};

export default LogViewer;
